import React, { useMemo } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Badge } from "./ui/badge";
import { useApp } from "../context/AppContext";
import { t } from "../lib/i18n";

const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

/**
 * Right-side drawer showing all transactions inside a single category (last 30
 * days). Triggered by clicking a pie legend chip or a pie slice on the
 * Dashboard.
 */
const CategoryDrillDrawer = ({ open, onOpenChange, category, transactions = [], color = "#0AFFB0" }) => {
    const { language } = useApp();

    const catLabel = t(language, `cat_${category || "other"}`);

    // Filter to this category, last 30 days, sort by date desc.
    const rows = useMemo(() => {
        if (!category) return [];
        const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
        return transactions
            .filter((tx) => (tx.app_category || "other") === category && tx.amount > 0)
            .filter((tx) => {
                const ts = tx.date ? new Date(tx.date).getTime() : NaN;
                return isNaN(ts) ? true : ts >= cutoff;
            })
            .sort((a, b) => (a.date < b.date ? 1 : -1));
    }, [category, transactions]);

    const total = rows.reduce((s, tx) => s + Math.abs(tx.amount || 0), 0);
    const avg = rows.length > 0 ? total / rows.length : 0;

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent side="right" className="w-full sm:max-w-md flex flex-col overflow-hidden" data-testid="category-drill-drawer">
                <SheetHeader className="border-b border-[hsl(var(--border))] pb-4">
                    <div className="flex items-center gap-3">
                        <div
                            className="w-11 h-11 rounded-2xl grid place-items-center flex-shrink-0"
                            style={{ background: `${color}20`, boxShadow: `0 0 24px -6px ${color}66` }}
                        >
                            <span className="w-4 h-4 rounded-full" style={{ background: color, boxShadow: `0 0 10px ${color}` }} />
                        </div>
                        <div className="min-w-0 flex-1 text-left">
                            <div className="text-[10px] font-bold tracking-[0.22em] text-[hsl(var(--muted-foreground))] uppercase">
                                {language === "es" ? "CATEGORÍA" : "CATEGORY"}
                            </div>
                            <SheetTitle className="text-xl font-display font-bold tracking-tight text-[hsl(var(--foreground))]">
                                {t(language, "cat_drill_title").replace("{cat}", catLabel)}
                            </SheetTitle>
                            <SheetDescription>{t(language, "cat_drill_desc")}</SheetDescription>
                        </div>
                    </div>

                    <div className="mt-4 grid grid-cols-3 gap-2">
                        <StatChip label={t(language, "cat_drill_total")} value={fmt(total)} accent={color} />
                        <StatChip label={t(language, "cat_drill_count")} value={rows.length} />
                        <StatChip label={t(language, "cat_drill_avg")} value={fmt(avg)} />
                    </div>
                </SheetHeader>

                <div className="flex-1 overflow-y-auto -mx-6 px-6 pt-4">
                    {rows.length === 0 ? (
                        <div className="py-10 text-center text-sm text-[hsl(var(--muted-foreground))]" data-testid="cat-drill-empty">
                            {t(language, "cat_drill_empty")}
                        </div>
                    ) : (
                        <div className="divide-y divide-[hsl(var(--border))]">
                            {rows.map((tx) => (
                                <div key={tx.transaction_id} data-testid="cat-drill-row" className="flex items-start justify-between py-3">
                                    <div className="flex-1 min-w-0 pr-3">
                                        <div className="font-medium text-[hsl(var(--foreground))] truncate">
                                            {tx.merchant_name || tx.name}
                                        </div>
                                        <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5 flex items-center gap-2">
                                            <span>{tx.date?.slice(0, 10)}</span>
                                            <Badge variant="outline" className="text-[10px] font-medium tracking-wide">
                                                {catLabel}
                                            </Badge>
                                        </div>
                                    </div>
                                    <div className="font-semibold tabular-nums text-[hsl(var(--foreground))]">
                                        {fmt(Math.abs(tx.amount))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </SheetContent>
        </Sheet>
    );
};

const StatChip = ({ label, value, accent }) => (
    <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--secondary))]/40 p-3 text-center">
        <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]">{label}</div>
        <div
            className="mt-1 font-semibold tabular-nums text-sm"
            style={accent ? { color: accent } : undefined}
        >
            {value}
        </div>
    </div>
);

export default CategoryDrillDrawer;
